import {UncachedTransactionsContext} from '#/contexts/blockchain-transactions-provider';
import environment from '#/environment/environment';
import {useLatestApiTransactions} from '#/hooks/query/use-latest-transactions';
import {tradingBotAddresses} from '#/utils/trading-bot-addresses';
import {useContext} from 'react';
import {DisplayableShareLandTransaction, Land, isEqualLowerCase} from 'shareland-common';
import {Hex} from 'viem';

type UseApiAndContractTransactions = Pick<
  ReturnType<typeof useLatestApiTransactions>,
  'hasNextPage' | 'fetchNextPage' | 'isFetchingNextPage'
> & {
  data: DisplayableShareLandTransaction[] | undefined;
  isApiLoading: boolean;
  isContractLoading: boolean;
  hasNextPage: boolean;
};

const getWaitlistNetTransactions = (transactions: DisplayableShareLandTransaction[]) =>
  environment.isWaitlistNet
    ? transactions.filter(transaction =>
        tradingBotAddresses.some(address => isEqualLowerCase(address, transaction.account)),
      )
    : transactions;

export const useLandTransactions = (land: Land | undefined, take = 10): UseApiAndContractTransactions => {
  const {getLandTransactions} = useContext(UncachedTransactionsContext);

  const {
    data,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage = false,
  } = useLatestApiTransactions({
    landTokenSymbol: land?.tokenSymbol,
    take,
  });

  const uncachedTransaction = getLandTransactions(land?.id ?? 0, data?.pages[0]?.results[0]?.blockNumber);

  const allPagesDatabaseTransactions = data?.pages.map(elem => elem.results).flat() ?? [];
  const allTransactions = getWaitlistNetTransactions(
    uncachedTransaction?.concat(allPagesDatabaseTransactions) ?? allPagesDatabaseTransactions,
  );

  return {
    data: allTransactions,
    isApiLoading: isLoading,
    isFetchingNextPage,
    isContractLoading: false,
    hasNextPage,
    fetchNextPage,
  };
};

export const useAccountTransactions = (account: Hex | undefined, take = 10): UseApiAndContractTransactions => {
  // context
  const {getUserTransactions} = useContext(UncachedTransactionsContext);

  const {
    data,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage = false,
  } = useLatestApiTransactions({
    account,
    take,
    enabled: !!account,
  });

  const uncachedTransaction = getUserTransactions(account ?? '', data?.pages[0]?.results[0]?.blockNumber);

  const allPagesDatabaseTransactions = data?.pages.map(elem => elem.results).flat() ?? [];
  const allTransactions = getWaitlistNetTransactions(
    uncachedTransaction?.concat(allPagesDatabaseTransactions) ?? allPagesDatabaseTransactions,
  );

  return {
    data: allTransactions,
    isApiLoading: isLoading,
    isFetchingNextPage,
    isContractLoading: false,
    hasNextPage,
    fetchNextPage,
  };
};
